import { CodeKind, MethodKind, } from "./types.js";
export class AbiQuery {
    abi;
    #scope;
    #target;
    constructor(abi) {
        this.abi = abi;
        this.#scope = abi.defs;
    }
    get hasResult() {
        return !!this.#target;
    }
    allCode() {
        return this.#scope;
    }
    getCode() {
        assertExists(this.#target);
        return this.#target;
    }
    getClass() {
        assertClass(this.#target);
        return this.#target;
    }
    getClassFull() {
        const node = this.getClass();
        const parents = this.getClassParents();
        const fields = parents.flatMap(p => p.fields).concat(node.fields);
        const methods = parents.flatMap(p => p.methods).concat(node.methods);
        return { ...node, extends: 'Jig', fields, methods };
    }
    getFunction() {
        assertFunction(this.#target);
        return this.#target;
    }
    getInterface() {
        assertInterface(this.#target);
        return this.#target;
    }
    getInterfaceFull() {
        const node = this.getInterface();
        const parents = this.getInterfaceParents();
        const fields = parents.flatMap(p => p.fields).concat(node.fields);
        const methods = parents.flatMap(p => p.methods).concat(node.methods);
        return { ...node, fields, methods };
    }
    getObject() {
        assertObject(this.#target);
        return this.#target;
    }
    getField(q) {
        const fields = this.getFieldsFull();
        const field = typeof q === 'string' ? fields.find(n => n.name === q) : fields[q];
        assertExists(field, 'field');
        return field;
    }
    getFieldIdx(name) {
        const fields = this.getFieldsFull();
        const idx = fields.findIndex(f => f.name === name);
        assertExists(idx >= 0, 'field');
        return idx;
    }
    getFieldsFull() {
        assertNodeKind(this.#target, ['fields']);
        let fields;
        switch (this.#target.kind) {
            case CodeKind.CLASS:
                fields = this.getClassFull().fields;
                break;
            case CodeKind.INTERFACE:
                fields = this.getInterfaceFull().fields;
            default:
                fields = this.#target.fields;
        }
        // Filter out dupes
        return [...new Set(fields)];
    }
    getMethod(q) {
        const methods = this.getMethodsFull();
        const method = typeof q === 'string' ?
            methods.findLast(n => n.name === q) :
            methods.filter(publicMethodsOnly)[q];
        assertExists(method, 'method');
        return method;
    }
    getMethodIdx(name) {
        const methods = this.getMethodsFull();
        const idx = methods.filter(publicMethodsOnly).findLastIndex(m => m.name === name);
        assertExists(idx >= 0, 'method');
        return idx;
    }
    getMethodsFull() {
        assertNodeKind(this.#target, ['methods']);
        return isClass(this.#target) ?
            this.getClassFull().methods :
            this.getInterfaceFull().methods;
    }
    getClassInterfaces() {
        assertClass(this.#target);
        const query = new AbiQuery(this.abi).byKind([CodeKind.INTERFACE, CodeKind.PROXY_INTERFACE]);
        return this.#target.implements.map(name => {
            return query.byName(name).getCode();
        });
    }
    getClassParents() {
        assertClass(this.#target);
        const parents = [];
        while (isClass(this.#target)) {
            this.toClassParent();
            if (isClass(this.#target))
                parents.push(this.getClass());
        }
        return parents.reverse();
    }
    getInterfaceParents() {
        assertInterface(this.#target);
        const query = new AbiQuery(this.abi).byKind([CodeKind.INTERFACE, CodeKind.PROXY_INTERFACE]);
        return this.#target.extends.map(name => {
            return query.byName(name).getInterface();
        });
    }
    getIndex() {
        assertExists(this.#target);
        const idx = this.#scope.indexOf(this.#target);
        assertExists(idx >= 0);
        return idx;
    }
    byKind(kind) {
        this.#scope = this.#scope.filter(c => {
            return Array.isArray(kind) ? kind.includes(c.kind) : kind === c.kind;
        });
        this.#target = undefined;
        return this;
    }
    byIndex(idx) {
        this.#target = this.#scope[idx];
        return this;
    }
    byName(name) {
        this.#target = this.#scope.find(n => n.name === name);
        return this;
    }
    fromExports() {
        this.reset();
        this.#scope = this.abi.exports.map(i => this.abi.defs[i]);
        return this;
    }
    fromImports() {
        this.reset();
        this.#scope = this.abi.imports.map(i => this.abi.defs[i]);
        return this;
    }
    toClassParent() {
        assertClass(this.#target);
        const parentName = this.#target.extends;
        return this.fromExports().byName(parentName);
    }
    search(name) {
        this.reset();
        const match = name.match(/^(\w+)(_|\.)(\w+)$/);
        if (match?.length === 4) {
            const [_str, codeName, sep, propName] = match;
            this.byName(codeName);
            return sep === '_' ?
                this.getMethod(propName) :
                this.getField(propName);
        }
        else {
            return this.byName(name).getCode();
        }
    }
    reset() {
        this.#scope = this.abi.defs;
        this.#target = undefined;
        return this;
    }
}
export function assertExists(bool, msg = 'subject') {
    if (!bool)
        throw new Error(`not found: the ${msg} does not exist`);
}
export function assertNodeKind(node, keys, kind) {
    assertExists(node);
    if (!keys.every(k => k in node)) {
        throw new Error(`invalid type, keys not found: ${keys.join(', ')}`);
    }
    if (typeof kind !== 'undefined' && !('kind' in node && node.kind === kind)) {
        throw new Error(`invalid type, kind not matching: ${kind}`);
    }
}
export function assertClass(node) {
    assertNodeKind(node, ['fields', 'methods'], CodeKind.CLASS);
}
export function assertFunction(node) {
    assertNodeKind(node, ['args', 'rtype'], CodeKind.FUNCTION);
}
export function assertInterface(node) {
    assertNodeKind(node, ['fields', 'methods'], CodeKind.INTERFACE);
}
export function assertObject(node) {
    assertNodeKind(node, ['fields'], CodeKind.OBJECT);
}
export function assertProxy(node) {
    assertNodeKind(node, ['kind', 'pkg']);
}
export function assertClassLike(node) {
    assertNodeKind(node, ['methods', 'fields']);
}
export function assertFunctionLike(node) {
    assertNodeKind(node, ['args', 'rtype']);
}
export function assertField(node) {
    assertNodeKind(node, ['name', 'type']);
}
export function assertMethod(node) {
    assertNodeKind(node, ['kind', 'args', 'rtype']);
}
export function isNodeKind(node, keys, kind) {
    return !!node &&
        keys.every(k => k in node) && (typeof kind !== 'undefined' ? 'kind' in node && node.kind === kind : true);
}
export function isClass(node) {
    return isNodeKind(node, ['fields', 'methods'], CodeKind.CLASS);
}
export function isFunction(node) {
    return isNodeKind(node, ['args', 'rtype'], CodeKind.FUNCTION);
}
export function isInterface(node) {
    return isNodeKind(node, ['fields', 'methods'], CodeKind.INTERFACE);
}
export function isObject(node) {
    return isNodeKind(node, ['fields'], CodeKind.OBJECT);
}
export function isProxy(node) {
    return isNodeKind(node, ['kind', 'pkg']);
}
export function isClassLike(node) {
    return isNodeKind(node, ['fields']);
}
export function isFunctionLike(node) {
    return isNodeKind(node, ['args', 'rtype']);
}
export function isField(node) {
    return isNodeKind(node, ['name', 'type']);
}
export function isMethod(node) {
    return isNodeKind(node, ['kind', 'args', 'rtype']);
}
function publicMethodsOnly(m) {
    return (typeof m.kind === 'undefined' || m.kind === MethodKind.PUBLIC) && m.name !== 'constructor';
}
